import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { PackageCheck, Camera, Images, X, Sparkles, Plus, Trash2 } from "lucide-react";
import useSWR from "swr";
import Header from "@/components/Header";
import { Card, TextInput, NumberInput, TextArea, Select, Field, Badge, Button } from "@/components/ui-kit";
import { StickySubmit } from "./TemperatureForm";
import { createRecord, updateRecord, getMeta, uploadFile, extractAlbaraLines, listProviders } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { getUser } from "@/lib/auth";

const emptyLine = () => ({ product: "", quantity: "", unit: "", lot: "", expiry: "", product_type_id: "carn_au_peces" });

export default function ReceptionForm() {
  const navigate = useNavigate();
  const editRecord = useLocation().state?.editRecord;
  const { data: meta } = useSWR("meta", getMeta);
  const { data: providers } = useSWR("providers", listProviders);
  const today = new Date().toISOString().slice(0, 10);
  const signer = getUser()?.username || "";

  const [header, setHeader] = useState(editRecord
    ? { date: editRecord.date, provider: editRecord.provider || "", provider_id: editRecord.provider_id || "", delivery_note: editRecord.delivery_note || "", reception_temp: editRecord.reception_temp != null ? String(editRecord.reception_temp) : "", incident: editRecord.incident || "" }
    : { date: today, provider: "", provider_id: "", delivery_note: "", reception_temp: "", incident: "" });
  const [lines, setLines] = useState(() => {
    if (!editRecord) return [emptyLine()];
    if (editRecord.lines?.length) return editRecord.lines.map((l) => ({ product: l.product || "", quantity: l.quantity != null ? String(l.quantity) : "", unit: l.unit || "", lot: l.lot || "", expiry: l.expiry || "", product_type_id: l.product_type_id || "carn_au_peces" }));
    return [{ product: editRecord.product || "", quantity: editRecord.quantity || "", unit: "", lot: editRecord.lot || "", expiry: editRecord.expiry || "", product_type_id: editRecord.product_type_id || "carn_au_peces" }];
  });
  const [photos, setPhotos] = useState(editRecord?.photos || []);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const productTypes = meta?.product_types || [];
  const setH = (k, v) => setHeader((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  // temperatura límit = la més estricta (menor) dels tipus de les línies
  const maxTemp = useMemo(() => {
    const temps = lines.map((l) => productTypes.find((p) => p.id === l.product_type_id)?.max_temp).filter((t) => t != null);
    return temps.length ? Math.min(...temps) : 4;
  }, [lines, productTypes]);
  const tempNum = parseFloat(header.reception_temp);
  const validation = isNaN(tempNum) ? null : tempNum <= maxTemp ? "OK" : "DEFICIENCIA";

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const f = await resizeImage(file, 1600, 0.75);
        const { url } = await uploadFile(f);
        setPhotos((p) => [...p, url]);
      }
    } catch { toast.error("No s'ha pogut pujar la foto"); }
    finally { setUploading(false); }
  };
  const removePhoto = (url) => setPhotos((p) => p.filter((u) => u !== url));

  const extract = async () => {
    if (!photos.length) return;
    setExtracting(true);
    try {
      const d = await extractAlbaraLines(photos[0], header.provider_id || undefined);
      setHeader((p) => ({
        ...p,
        provider: d.provider || p.provider,
        delivery_note: d.delivery_note || p.delivery_note,
        date: d.date || p.date,
        reception_temp: d.reception_temp != null && d.reception_temp !== "" ? String(d.reception_temp) : p.reception_temp,
      }));
      if (Array.isArray(d.lines) && d.lines.length) {
        setLines(d.lines.map((l) => ({
          product: l.product || "", quantity: l.quantity != null ? String(l.quantity) : "",
          unit: l.unit || "", lot: l.lot || "", expiry: l.expiry || "", product_type_id: "carn_au_peces",
        })));
      }
      toast.success(`Extret: ${d.lines?.length || 0} línia(es). Revisa-les.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No s'han pogut extreure les dades");
    } finally { setExtracting(false); }
  };

  const submit = async () => {
    const validLines = lines.filter((l) => l.product.trim());
    if (!header.provider.trim() || !header.delivery_note.trim() || isNaN(tempNum) || validLines.length === 0)
      return toast.error("Falta proveïdor, núm. albarà, temperatura o alguna línia");
    setSubmitting(true);
    try {
      const payload = {
        date: header.date, provider: header.provider.trim(), provider_id: header.provider_id || null,
        product: validLines[0].product, quantity: validLines[0].quantity, lot: validLines[0].lot, expiry: validLines[0].expiry,
        product_type_id: validLines[0].product_type_id,
        lines: validLines, reception_temp: tempNum, max_temp: maxTemp,
        delivery_note: header.delivery_note.trim(), validation, incident: header.incident, photos, signer,
      };
      if (editRecord) await updateRecord("reception", editRecord.id, payload);
      else await createRecord("reception", payload);
      toast.success(editRecord ? "Recepció modificada" : "Recepció registrada");
      navigate("/history?type=reception");
    } catch { toast.error("Error desant el registre"); }
    finally { setSubmitting(false); }
  };

  return (
    <>
      <Header title="Recepció" />
      <div className="mx-auto max-w-2xl px-4 pb-32 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ink text-white"><PackageCheck size={22} /></div>
          <div><div className="label-eyebrow">Per entrada</div><h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">Recepció de mercaderies</h2></div>
        </div>

        <div className="mt-6 space-y-4">
          <Card>
            <div className="label-eyebrow mb-2">Foto de l'albarà</div>
            <div className="flex flex-wrap gap-3">
              {photos.map((url) => (
                <div key={url} className="relative h-20 w-20">
                  <img src={url} alt="Albarà" className="h-20 w-20 rounded-xl border border-line object-cover" />
                  <button type="button" onClick={() => removePhoto(url)} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-ember text-white shadow-card"><X size={14} /></button>
                </div>
              ))}
              <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-line bg-white text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                <Camera size={20} />
                <span className="text-[10px] font-semibold uppercase tracking-wide">{uploading ? "Pujant…" : "Càmera"}</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFiles} disabled={uploading} />
              </label>
              <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-line bg-white text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                <Images size={20} />
                <span className="text-[10px] font-semibold uppercase tracking-wide">Galeria</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} disabled={uploading} />
              </label>
            </div>
            {photos.length > 0 && (
              <Button onClick={extract} disabled={extracting} testid="albara-extract" className="mt-3 w-full">
                <Sparkles size={16} /> {extracting ? "Extraient dades…" : "Extreure dades (IA)"}
              </Button>
            )}
          </Card>

          <Card><div className="space-y-4">
            <Field label="Data"><input type="date" value={header.date} onChange={(e) => setH("date", e.target.value)} className="h-14 w-full rounded-xl border border-line bg-white px-4 text-base text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20" /></Field>
            <Field label="Proveïdor">
              <Select value={header.provider_id} onChange={(e) => {
                const p = providers?.find((x) => x.id === e.target.value);
                setHeader((h) => ({ ...h, provider_id: e.target.value, provider: p ? p.name : h.provider }));
              }}>
                <option value="">— Tria proveïdor —</option>
                {providers?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
              {!header.provider_id && (
                <div className="mt-2"><TextInput placeholder="…o escriu el nom" value={header.provider} onChange={(e) => setH("provider", e.target.value)} /></div>
              )}
            </Field>
            <Field label="Núm. albarà"><TextInput placeholder="A-12345" value={header.delivery_note} onChange={(e) => setH("delivery_note", e.target.value)} /></Field>
          </div></Card>

          <Card>
            <div className="mb-3 flex items-center justify-between">
              <div className="label-eyebrow">Línies ({lines.length})</div>
              <button type="button" onClick={addLine} className="inline-flex items-center gap-1 text-sm font-semibold text-terracotta"><Plus size={16} /> Afegir línia</button>
            </div>
            <div className="space-y-4">
              {lines.map((l, i) => (
                <div key={i} className="rounded-xl border border-line bg-muted/40 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-bold text-ink-soft">#{i + 1}</span>
                    {lines.length > 1 && <button type="button" onClick={() => removeLine(i)} className="text-ink-soft hover:text-ember"><Trash2 size={15} /></button>}
                  </div>
                  <div className="space-y-2">
                    <TextInput placeholder="Producte" value={l.product} onChange={(e) => setLine(i, "product", e.target.value)} />
                    <div className="grid grid-cols-2 gap-2">
                      <TextInput placeholder="Quantitat" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} />
                      <TextInput placeholder="Unitat (kg, u…)" value={l.unit} onChange={(e) => setLine(i, "unit", e.target.value)} />
                    </div>
                    <Select value={l.product_type_id} onChange={(e) => setLine(i, "product_type_id", e.target.value)}>
                      {productTypes.map((p) => <option key={p.id} value={p.id}>{p.name} (màx {p.max_temp}°C)</option>)}
                    </Select>
                    <div className="grid grid-cols-2 gap-2">
                      <TextInput placeholder="Lot" value={l.lot} onChange={(e) => setLine(i, "lot", e.target.value)} />
                      <input type="date" value={l.expiry} onChange={(e) => setLine(i, "expiry", e.target.value)} className="h-14 w-full rounded-xl border border-line bg-white px-4 text-base text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card><Field label={"Temperatura de recepció (°C) — Límit: " + maxTemp + "°C"}>
            <div className="flex items-center gap-3">
              <NumberInput placeholder="0.0" value={header.reception_temp} onChange={(e) => setH("reception_temp", e.target.value)} className="flex-1" />
              {validation && <Badge status={validation} />}
            </div></Field></Card>

          <Card><Field label="Incidència detectada (opcional)">
            <TextArea placeholder="Si hi ha algun problema, descriu-lo aquí" value={header.incident} onChange={(e) => setH("incident", e.target.value)} /></Field></Card>
        </div>
      </div>
      <StickySubmit onClick={submit} disabled={submitting || uploading || extracting} label={submitting ? "Desant…" : editRecord ? "Desar canvis" : "Desar recepció"} />
    </>
  );
}
